import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import lodashCompact from 'lodash/fp/compact';

import { SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_LEASE_START_DATE } from 'api/utilityActionTypes';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityActivationActions from 'client/actions/UtilityActivation';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const formFields = [
  {
    id: 'leaseStartDate',
    title: 'Lease Start Date',
    type: 'date_input',
    properties: {
      dateWithoutTime: true,
    },
    validations: ['required'],
  },
];

const SrUtilitySetupEditLeaseStartDateModal = ({ utilityActivation, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();

  const onSubmit = (formValues) => {
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_LEASE_START_DATE,
      payload: {
        utilityActivationId: utilityActivation.id,
        leaseStartDate: formValues.leaseStartDate,
      },
    })
      .then(() => {
        return dispatch(UtilityActivationActions.getByShortId, {
          utilityActivationShortId: utilityActivation.shortId,
          forceRefresh: true,
        });
      })
      .then(() => {
        setIsSubmitting(false);
        onClose();
      });
  };

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: formFields,
        defaultValues: {
          leaseStartDate: utilityActivation.leaseStartDate,
        },
      })}
      validate={createFormValidationFn(formFields)}
      onSubmit={onSubmit}
      render={(formikBag) => {
        return (
          <SrSimpleMessageModal
            innerStyle={{ padding: '10px' }}
            headline={
              utilityActivation.leaseStartDate ? 'Edit lease start date' : 'Add lease start date'
            }
            underline
            messageClassName="noAlign"
            message={
              <SrBox margin="0 0 12px">
                {formFields.map((formField) => (
                  <SrFormikField
                    key={formField.id}
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                ))}
              </SrBox>
            }
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            minWidth={375}
            srMultiButtons={lodashCompact([
              {
                buttonText: utilityActivation.leaseStartDate ? 'Update' : 'Add',
                onButtonClick: formikBag.handleSubmit,
                buttonTheme: SR_BUTTON_THEME_ADOBE_GREEN,
                buttonDisabled: isSubmitting,
              },
              utilityActivation.leaseStartDate && {
                buttonText: 'Cancel',
                onButtonClick: onClose,
                buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
              },
            ])}
          />
        );
      }}
    />
  );
};

SrUtilitySetupEditLeaseStartDateModal.propTypes = {
  utilityActivation: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

SrUtilitySetupEditLeaseStartDateModal.displayName = 'SrUtilitySetupEditLeaseStartDateModal';

export default SrUtilitySetupEditLeaseStartDateModal;
